// Importar Dexie
import Dexie from 'dexie';

/**
 * Base de datos IndexedDB para el sistema de chat
 * Configurada con Dexie.js para un manejo robusto del caché
 */
class ChatDatabase extends Dexie {
  constructor() {
    super('VenttifyChatDB');
    
    // Definir esquemas de tablas con índices optimizados
    this.version(1).stores({
      // Tabla de tickets con índices para búsquedas optimizadas
      tickets: '++id, uuid, contactId, status, userId, queueId, companyId, createdAt, updatedAt, lastMessageAt, [companyId+status], [companyId+contactId], [companyId+userId]',
      
      // Tabla de mensajes con índices para ordenamiento y búsquedas
      messages: '++id, ticketId, contactId, companyId, fromMe, mediaType, ack, createdAt, updatedAt, isDeleted, [ticketId+createdAt], [companyId+ticketId], [companyId+createdAt]',
      
      // Tabla de contactos para información completa
      contacts: '++id, number, name, email, companyId, profilePicUrl, isGroup, createdAt, updatedAt, [companyId+number], [companyId+name]',
      
      // Tabla de colas/queues
      queues: '++id, name, color, greetingMessage, companyId, isActive, [companyId+isActive]',
      
      // Tabla de usuarios para información de agentes
      users: '++id, name, email, profile, companyId, profilePicUrl, isOnline, [companyId+isOnline]',
      
      // Tabla de metadatos para control de sincronización
      syncMeta: '++id, tableName, lastSync, version, companyId, [companyId+tableName]'
    });

    // Definir hooks para automáticamente agregar timestamps
    this.tickets.hook('creating', function (primKey, obj, trans) {
      obj.createdAt = obj.createdAt || new Date().toISOString();
      obj.updatedAt = obj.updatedAt || new Date().toISOString();
    });

    this.tickets.hook('updating', function (modifications, primKey, obj, trans) {
      modifications.updatedAt = new Date().toISOString();
    });

    this.messages.hook('creating', function (primKey, obj, trans) {
      obj.createdAt = obj.createdAt || new Date().toISOString();
      obj.updatedAt = obj.updatedAt || new Date().toISOString();
    });

    this.messages.hook('updating', function (modifications, primKey, obj, trans) {
      modifications.updatedAt = new Date().toISOString();
    });

    this.contacts.hook('creating', function (primKey, obj, trans) {
      obj.createdAt = obj.createdAt || new Date().toISOString();
      obj.updatedAt = obj.updatedAt || new Date().toISOString();
    });

    this.contacts.hook('updating', function (modifications, primKey, obj, trans) {
      modifications.updatedAt = new Date().toISOString();
    });
  }
}

// Crear instancia única de la base de datos
const db = new ChatDatabase();

// Función para obtener el companyId del localStorage
const getCompanyId = () => {
  return parseInt(localStorage.getItem('companyId')) || 0;
};

/**
 * Utilidades para manejo de errores y logging
 */
const dbLogger = {
  info: (operation, data) => {
    console.log(`🗄️ [DB] ${operation}:`, data);
  },
  error: (operation, error) => {
    console.error(`❌ [DB] Error en ${operation}:`, error);
  },
  warn: (operation, warning) => {
    console.warn(`⚠️ [DB] Advertencia en ${operation}:`, warning);
  }
};

/**
 * Funciones de utilidad para validación y limpieza
 */
const dbUtils = {
  // Validar que un objeto tenga las propiedades requeridas
  validateTicket: (ticket) => {
    return ticket && 
           typeof ticket.id !== 'undefined' && 
           typeof ticket.contactId !== 'undefined' && 
           typeof ticket.companyId !== 'undefined';
  },

  validateMessage: (message) => {
    return message && 
           typeof message.id !== 'undefined' && 
           typeof message.ticketId !== 'undefined' && 
           typeof message.companyId !== 'undefined';
  },

  validateContact: (contact) => {
    return contact && 
           typeof contact.id !== 'undefined' && 
           typeof contact.companyId !== 'undefined';
  },

  // Limpiar propiedades undefined antes de guardar
  cleanObject: (obj) => {
    const cleaned = {};
    Object.keys(obj).forEach(key => {
      if (obj[key] !== undefined && obj[key] !== null) {
        cleaned[key] = obj[key];
      }
    });
    return cleaned;
  },

  // Obtener fecha actual en formato ISO
  now: () => new Date().toISOString(),

  // Verificar si una fecha es válida
  isValidDate: (dateString) => {
    return dateString && !isNaN(new Date(dateString).getTime());
  }
};

/**
 * Funciones para manejo de metadatos de sincronización
 */
const syncMetaUtils = {
  // Actualizar timestamp de última sincronización
  updateLastSync: async (tableName) => {
    const companyId = getCompanyId();
    try {
      await db.syncMeta.put({
        tableName,
        companyId,
        lastSync: dbUtils.now(),
        version: 1
      });
      dbLogger.info('updateLastSync', { tableName, companyId });
    } catch (error) {
      dbLogger.error('updateLastSync', error);
    }
  },

  // Obtener timestamp de última sincronización
  getLastSync: async (tableName) => {
    const companyId = getCompanyId();
    try {
      const meta = await db.syncMeta
        .where('[companyId+tableName]')
        .equals([companyId, tableName])
        .first();
      return meta?.lastSync || null;
    } catch (error) {
      dbLogger.error('getLastSync', error);
      return null;
    }
  },

  // Limpiar metadatos antiguos
  cleanOldMeta: async (daysOld = 30) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    const cutoffISO = cutoffDate.toISOString();

    try {
      const deleted = await db.syncMeta
        .where('lastSync')
        .below(cutoffISO)
        .delete();
      dbLogger.info('cleanOldMeta', { deleted, cutoffDate });
      return deleted;
    } catch (error) {
      dbLogger.error('cleanOldMeta', error);
      return 0;
    }
  }
};

// Exportar la instancia de la base de datos y utilidades
export { 
  db, 
  dbLogger, 
  dbUtils, 
  syncMetaUtils, 
  getCompanyId 
};

export default db; 